import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { configureAuth } from '../api/client';
import * as authApi from '../api/auth';
import * as impersonationApi from '../api/impersonation';
import type { ImpersonatorInfo } from '../api/impersonation';
import { msalInstance, clearMicrosoftRedirectPending } from '../lib/msal';
import { setMonitoringUser } from '../lib/newrelic';
import type { User } from '../types/user';
import { decodeUser } from '../utils/decodeApiStrings';
import { decodeHtml } from '../lib/planningCycleLabels';

interface AuthContextValue {
  currentUser: User | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ mustChangePassword?: boolean }>;
  loginWithMicrosoft: (idToken: string) => Promise<void>;
  logout: () => Promise<void>;
  // ── Debug / Assume-User (impersonation) — NON-PRODUCTION ONLY ──
  /** True when the backend reports the debug feature is available (non-prod, opted in). */
  debugModeAvailable: boolean;
  /** Original admin identity while impersonating; null otherwise. */
  impersonator: ImpersonatorInfo | null;
  /** Convenience flag — true while acting as another user. */
  isImpersonating: boolean;
  /** Begin acting as another user (admin only; backend enforces). */
  startImpersonation: (userId: number, reason?: string) => Promise<void>;
  /** Stop impersonating and return to the original admin identity. */
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Auth tokens live in localStorage so they are shared across tabs in the same
// browser profile. This enables opening a new tab (copy-paste URL) without
// requiring the user to log in again.
//
// LOGOUT_REASON_KEY intentionally stays in sessionStorage — it is a per-tab
// message that should only appear in the tab that triggered the sign-out.
const TOKEN_KEY = 'budgetAppToken';
const REFRESH_KEY = 'budgetAppRefreshToken';
const USER_KEY = 'budgetAppUser';
const LOGOUT_REASON_KEY = 'budgetAppLogoutReason';
// Original admin identity while impersonating (debug mode). Persisted so the
// banner renders immediately on reload before the /state probe resolves.
const IMPERSONATOR_KEY = 'budgetAppImpersonator';
// Persisted to localStorage so the 2h idle check survives browser close/reopen.
const LAST_ACTIVITY_KEY = 'budgetAppLastActivity';

// AC: "Session expires after 2 hours of inactivity with redirect to login."
const INACTIVITY_TIMEOUT_MS = 2 * 60 * 60 * 1000;
// Throttle in-memory activity ref writes — one write per 5s is plenty for the
// 60s idle poll. sub-microsecond cost; no React state involved.
const ACTIVITY_THROTTLE_MS = 5_000;
// Throttle localStorage writes separately — once per minute reduces I/O while
// still being accurate enough for the 2h stale-session-on-reopen check.
// Worst-case staleness is ~59s, negligible against a 2h window.
const LS_ACTIVITY_THROTTLE_MS = 60_000;
// How often we poll for the inactivity threshold. 60s means logout fires at
// most 60s after the 2h mark — acceptable for a session timer.
const INACTIVITY_POLL_MS = 60_000;
// Deliberately omitting `mousemove`: it fires ~60Hz during normal pointer
// motion. mousedown/keydown/scroll/touchstart cover every real interaction
// without paying that cost. visibilitychange catches tab focus returns.
const ACTIVITY_EVENTS: Array<keyof DocumentEventMap> = [
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'visibilitychange',
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // ── Debug / Assume-User state ──
  const [debugModeAvailable, setDebugModeAvailable] = useState(false);
  const [impersonator, setImpersonator] = useState<ImpersonatorInfo | null>(() => {
    try {
      const raw = localStorage.getItem(IMPERSONATOR_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as ImpersonatorInfo;
      return {
        ...parsed,
        full_name: decodeHtml(parsed.full_name),
        display_name: parsed.display_name != null ? decodeHtml(parsed.display_name) : parsed.display_name,
        email: decodeHtml(parsed.email),
      };
    } catch {
      return null;
    }
  });
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inactivityExpiringRef = useRef(false);
  const lastActivityRef = useRef<number>(0);
  const lastLSActivityWriteRef = useRef<number>(0);

  // ── Wire up API client auth hooks ──
  useEffect(() => {
    configureAuth({
      getAccessToken: () => accessToken ?? localStorage.getItem(TOKEN_KEY),
      getRefreshToken: () => localStorage.getItem(REFRESH_KEY),
      onTokenRefreshed: (token: string) => {
        setAccessToken(token);
        localStorage.setItem(TOKEN_KEY, token);
      },
      onAuthFailed: () => {
        // No-op: don't auto-logout on API 401s.
        // Session is preserved; user stays logged in until they
        // explicitly logout or the access token fully expires.
        console.warn('Auth failed event — token may need refresh');
      },
    });
  }, [accessToken]);

  // ── Auto-refresh timer ──
  const scheduleRefresh = useCallback((expiresIn: number) => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    // Refresh 2 minutes before expiry, or at 50% if < 4 minutes
    const refreshMs = expiresIn > 240
      ? (expiresIn - 120) * 1000
      : (expiresIn * 0.5) * 1000;

    refreshTimerRef.current = setTimeout(async () => {
      try {
        const refreshTok = localStorage.getItem(REFRESH_KEY);
        if (!refreshTok) return;
        const result = await authApi.refreshToken(refreshTok);
        setAccessToken(result.access_token);
        localStorage.setItem(TOKEN_KEY, result.access_token);
        scheduleRefresh(result.expires_in);
      } catch {
        clearSession();
      }
    }, refreshMs);
  }, []);

  function clearSession() {
    setCurrentUser(null);
    setAccessToken(null);
    setImpersonator(null);
    setDebugModeAvailable(false);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(IMPERSONATOR_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    // Clear the Microsoft redirect flag so a logout never leaves a stale flag
    // that would show the spinner instead of the login form on next visit.
    clearMicrosoftRedirectPending();
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    if (inactivityTimerRef.current) clearInterval(inactivityTimerRef.current);
  }

  function setSession(token: string, refreshTok: string, user: User, expiresIn: number) {
    const decodedUser = decodeUser(user);
    setAccessToken(token);
    setCurrentUser(decodedUser);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_KEY, refreshTok);
    localStorage.setItem(USER_KEY, JSON.stringify(decodedUser));
    // Stamp activity on login so the 2h idle clock starts from now.
    const now = Date.now();
    inactivityExpiringRef.current = false;
    lastActivityRef.current = now;
    lastLSActivityWriteRef.current = now;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    scheduleRefresh(expiresIn);
  }

  // ── Restore session on mount ──
  // Behaviour on a new tab (copy-paste URL):
  //   1. Read tokens from localStorage (shared across tabs).
  //   2. If lastActivity is stale (> 2h), treat the session as expired and
  //      redirect to login — this covers browser close + reopen after long idle.
  //   3. If fresh, keep isLoading=true and verify by calling /auth/refresh so
  //      the full-page loader is shown while we confirm the session is still
  //      valid. Only set isLoading=false once we know the outcome.
  useEffect(() => {
    const savedToken = localStorage.getItem(TOKEN_KEY);
    const savedUser = localStorage.getItem(USER_KEY);
    const savedRefresh = localStorage.getItem(REFRESH_KEY);
    const lastActivityRaw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const lastActivity = Number(lastActivityRaw);
    const hasValidLastActivity = Number.isFinite(lastActivity) && lastActivity > 0;

    // Guard: stale/unknown activity session from before browser was closed.
    // Fail closed: if activity metadata is missing/corrupt, force re-auth.
    if (
      savedToken &&
      savedUser &&
      (!hasValidLastActivity || Date.now() - lastActivity >= INACTIVITY_TIMEOUT_MS)
    ) {
      sessionStorage.setItem(LOGOUT_REASON_KEY, 'inactive');
      clearSession();
      setIsLoading(false);
      return;
    }

    if (savedToken && savedUser) {
      try {
        // Optimistically populate state so isAuthenticated flips to true while
        // we verify. isLoading stays true so ProtectedRoute shows the loader
        // rather than the app until the refresh resolves.
        setAccessToken(savedToken);
        // Re-decode on restore so legacy localStorage entries with escaped
        // identity fields render correctly in the header.
        setCurrentUser(decodeUser(JSON.parse(savedUser) as User));
        // Reset the activity clock to now — don't count idle time while the
        // browser was closed as in-tab inactivity.
        const now = Date.now();
        lastActivityRef.current = now;
        lastLSActivityWriteRef.current = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      } catch {
        // Corrupted user JSON — bail out cleanly.
        clearSession();
        setIsLoading(false);
        return;
      }

      if (savedRefresh) {
        authApi.refreshToken(savedRefresh)
          .then((result) => {
            setAccessToken(result.access_token);
            localStorage.setItem(TOKEN_KEY, result.access_token);
            scheduleRefresh(result.expires_in);
            setIsLoading(false);
          })
          .catch(() => {
            // Refresh token expired or invalid — session cannot be restored.
            sessionStorage.setItem(LOGOUT_REASON_KEY, 'session_expired');
            clearSession();
            setIsLoading(false);
          });
      } else {
        // No refresh token but we have an access token — let it ride.
        // The API interceptor will handle 401s if the access token is expired.
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, [scheduleRefresh]);

  // ── Tag New Relic Browser data with the signed-in user ──
  // Keyed on id/email so it covers login, session restore, impersonation swaps,
  // and logout (currentUser → null clears the identity). No-op when the agent
  // isn't configured.
  useEffect(() => {
    setMonitoringUser(currentUser?.id ?? null, currentUser?.email ?? null);
  }, [currentUser?.id, currentUser?.email]);

  // Refresh role_keys + has_bridge_access from the permissions endpoint (session restore).
  useEffect(() => {
    if (!accessToken || !currentUser?.id) return;
    authApi.getPermissions()
      .then((perms) => {
        setCurrentUser((prev) => {
          if (!prev) return prev;
          const updated: User = {
            ...prev,
            role_keys: perms.role_keys,
            is_admin: perms.is_admin,
            has_bridge_access: perms.has_bridge_access,
          };
          localStorage.setItem(USER_KEY, JSON.stringify(updated));
          return updated;
        });
      })
      .catch(() => { /* permissions refresh is best-effort */ });
  }, [accessToken, currentUser?.id]);

  // ── Cross-tab logout propagation ──
  // localStorage `storage` events fire in every OTHER tab in the same origin
  // when a key is changed. When TOKEN_KEY is removed (logout or idle expire in
  // any tab), mirror the sign-out in this tab without requiring a page reload.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TOKEN_KEY && e.newValue === null) {
        // Another tab cleared the session — clear React state here too.
        // localStorage is already empty; just wipe in-memory state and timers.
        setCurrentUser(null);
        setAccessToken(null);
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        if (inactivityTimerRef.current) {
          clearInterval(inactivityTimerRef.current);
          inactivityTimerRef.current = null;
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ── Login ──
  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password);
    setSession(result.access_token, result.refresh_token, result.user, result.expires_in);
    return { mustChangePassword: result.must_change_password };
  }, [scheduleRefresh]);

  const loginWithMicrosoft = useCallback(async (idToken: string) => {
    const result = await authApi.loginWithMicrosoft(idToken);
    setSession(result.access_token, result.refresh_token, result.user, result.expires_in);
  }, [scheduleRefresh]);

  // ── Debug / Assume-User (impersonation) ─────────────────────────────────
  // Probe availability + reconcile impersonation status whenever the
  // authenticated identity changes. A 404 means the feature is unavailable
  // (production or env not opted in) — fail closed by hiding it.
  useEffect(() => {
    if (!currentUser?.id) {
      setDebugModeAvailable(false);
      return;
    }
    let cancelled = false;
    impersonationApi
      .getImpersonationState()
      .then((state) => {
        if (cancelled) return;
        setDebugModeAvailable(!!state.enabled);
        // Reconcile impersonator from the verified token (authoritative),
        // covering reloads where localStorage might be stale or missing.
        if (state.is_impersonating && state.impersonator) {
          const impersonatorDecoded = {
            ...state.impersonator,
            full_name: decodeHtml(state.impersonator.full_name),
            display_name: state.impersonator.display_name != null
              ? decodeHtml(state.impersonator.display_name)
              : state.impersonator.display_name,
            email: decodeHtml(state.impersonator.email),
          };
          setImpersonator(impersonatorDecoded);
          localStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(impersonatorDecoded));
        } else {
          setImpersonator(null);
          localStorage.removeItem(IMPERSONATOR_KEY);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // 404 / error ⇒ feature unavailable. Clear any stale banner state.
        setDebugModeAvailable(false);
        setImpersonator(null);
        localStorage.removeItem(IMPERSONATOR_KEY);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  const startImpersonation = useCallback(async (userId: number, reason?: string) => {
    const result = await impersonationApi.startImpersonation(userId, reason);
    // Swap the session to the impersonated user. Existing app code reading
    // currentUser now transparently sees the effective user.
    setSession(result.access_token, result.refresh_token, result.user, result.expires_in);
    if (result.impersonator) {
      setImpersonator(result.impersonator);
      localStorage.setItem(IMPERSONATOR_KEY, JSON.stringify(result.impersonator));
    }
  }, []);

  const stopImpersonation = useCallback(async () => {
    const result = await impersonationApi.stopImpersonation();
    setSession(result.access_token, result.refresh_token, result.user, result.expires_in);
    setImpersonator(null);
    localStorage.removeItem(IMPERSONATOR_KEY);
  }, []);

  const logoutFn = useCallback(async () => {
    // Best-effort: wipe MSAL's persistent sessionStorage cache so stale account
    // entries don't appear in getAllAccounts(). The real guard against ghost-
    // login is in LoginPage: it only calls handleRedirectPromise() when the
    // current URL contains Microsoft OAuth redirect params (consumeMicrosoftRedirect).
    try {
      await msalInstance?.clearCache();
    } catch {
      // Non-fatal.
    }
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors.
    }
    clearSession();
  }, []);

  // ── Inactivity timeout (2h) ─────────────────────────────────────────────
  // Auto-logout after INACTIVITY_TIMEOUT_MS with no user input. ProtectedRoute
  // sees !isAuthenticated and redirects to /login; LoginPage reads
  // LOGOUT_REASON_KEY from sessionStorage to surface the reason.
  //
  // Design notes:
  //   - Per-tab scope: each tab runs its own idle timer. The first tab to hit
  //     2h idle clears localStorage → storage event propagates logout to all
  //     other open tabs automatically.
  //   - Keyed on currentUser?.id (not accessToken) so silent token refreshes
  //     do NOT reset the inactivity clock.
  //   - Activity handlers are passive + throttled (5s in-memory, 60s LS write)
  //     — no re-renders, no allocations, no React state churn.
  //   - Exactly ONE outstanding interval at all times (60s setInterval poll)
  //     instead of clear+setTimeout on every event.
  //   - AbortController removes all listeners with a single abort.
  useEffect(() => {
    const userId = currentUser?.id;
    if (!userId) return;

    const hasExpired = (now: number) => now - lastActivityRef.current >= INACTIVITY_TIMEOUT_MS;

    const expire = async () => {
      if (inactivityExpiringRef.current) return;
      inactivityExpiringRef.current = true;
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      sessionStorage.setItem(LOGOUT_REASON_KEY, 'inactive');
      // Local sign-out is priority; backend logout is best effort.
      void authApi.logout().catch(() => {});
      clearSession();
    };

    const onActivity = (event: Event) => {
      // Treat tab-hide as neutral: returning to a long-idle tab should NOT reset
      // the clock before we evaluate expiry.
      if (event.type === 'visibilitychange' && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (hasExpired(now)) {
        void expire();
        return;
      }

      // In-memory update: 5s throttle — keeps the per-tab idle poll accurate.
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;

      // localStorage write: 60s throttle — persists activity for the
      // browser-close + reopen stale-session check with minimal I/O.
      if (now - lastLSActivityWriteRef.current >= LS_ACTIVITY_THROTTLE_MS) {
        lastLSActivityWriteRef.current = now;
        localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      }
    };

    const now = Date.now();
    lastActivityRef.current = now;
    lastLSActivityWriteRef.current = now;
    inactivityExpiringRef.current = false;
    localStorage.setItem(LAST_ACTIVITY_KEY, String(now));
    const controller = new AbortController();
    ACTIVITY_EVENTS.forEach((evt) =>
      document.addEventListener(evt, onActivity, {
        passive: true,
        signal: controller.signal,
      })
    );

    inactivityTimerRef.current = setInterval(() => {
      if (hasExpired(Date.now())) {
        void expire();
      }
    }, INACTIVITY_POLL_MS);

    return () => {
      controller.abort();
      if (inactivityTimerRef.current) {
        clearInterval(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [currentUser?.id]);

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        accessToken,
        isAuthenticated: !!accessToken && !!currentUser,
        isLoading,
        login,
        loginWithMicrosoft,
        logout: logoutFn,
        debugModeAvailable,
        impersonator,
        isImpersonating: !!impersonator,
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
